import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2, Copy, Lock, QrCode } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

export default function TwoFASettings({ accent = "#A3E635" }) {
  const { user, checkAuth } = useAuth();
  const { t } = useI18n();
  const enabled = !!user?.totp_enabled;
  const [mode, setMode] = useState("idle"); // idle | setup | disable
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");

  const errMsg = (e, fb) => {
    const d = e?.response?.data?.detail;
    return typeof d === "string" ? d : fb;
  };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable-start");
      setQr(data.qr); setSecret(data.secret); setCode(""); setMode("setup");
    } catch (e) { toast.error(errMsg(e, t("Einrichtung konnte nicht gestartet werden"))); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (code.trim().replace(/\s/g, "").length < 6) { toast.error(t("Bitte 6-stelligen Code aus Ihrer App eingeben")); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable-confirm", { code: code.trim() });
      toast.success(t("Zwei-Faktor-Login ist jetzt aktiv"));
      setMode("idle"); setQr(""); setSecret(""); setCode("");
      await checkAuth();
    } catch (e) { toast.error(errMsg(e, t("Code ungültig"))); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!password) { toast.error(t("Bitte Ihr Passwort eingeben")); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password });
      toast.success(t("Zwei-Faktor-Login wurde deaktiviert"));
      setMode("idle"); setPassword("");
      await checkAuth();
    } catch (e) { toast.error(errMsg(e, t("Passwort falsch"))); }
    finally { setBusy(false); }
  };

  const copySecret = () => { navigator.clipboard?.writeText(secret); toast.success(t("Schlüssel kopiert")); };

  const btn = (bg) => ({ background: bg, color: bg === accent ? "#000" : undefined });

  return (
    <Card className="p-6" data-testid="twofa-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: enabled ? `${accent}1A` : "rgba(251,191,36,0.1)" }}>
            {enabled ? <ShieldCheck className="w-5 h-5" style={{ color: accent }} /> : <ShieldAlert className="w-5 h-5 text-amber-300" />}
          </div>
          <div>
            <h3 className="font-bold">{t("Zwei-Faktor-Login (2FA)")}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{t("Zusätzlicher Schutz über eine Authenticator-App (z. B. Google Authenticator, Authy).")}</p>
          </div>
        </div>
        {enabled ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border"
            style={{ borderColor: `${accent}66`, background: `${accent}1A`, color: accent }} data-testid="twofa-status-on">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("Aktiv")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300" data-testid="twofa-status-off">
            <ShieldAlert className="w-3.5 h-3.5" /> {t("Nicht aktiv")}
          </span>
        )}
      </div>

      {/* IDLE: aktiv → deaktivieren anbieten / inaktiv → aktivieren anbieten */}
      {mode === "idle" && (
        <div>
          {enabled ? (
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-gray-400 flex-1 min-w-[200px]">{t("Ihr Konto ist mit Zwei-Faktor-Login geschützt. Beim nächsten Login fragen wir zusätzlich einen Code aus Ihrer App ab.")}</p>
              <button onClick={() => setMode("disable")} disabled={busy} data-testid="twofa-disable-start"
                className="border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-semibold py-2 px-4 rounded-lg text-sm disabled:opacity-60">
                {t("2FA deaktivieren")}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-gray-400 flex-1 min-w-[200px]">{t("2FA ist optional, wird aber empfohlen. Sie brauchen nur eine Authenticator-App auf Ihrem Handy.")}</p>
              <button onClick={start} disabled={busy} data-testid="twofa-enable-start" style={btn(accent)}
                className="font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("2FA aktivieren")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* SETUP: QR + Code bestätigen */}
      {mode === "setup" && (
        <div className="space-y-4" data-testid="twofa-setup-panel">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="bg-white p-3 rounded-xl shrink-0">
              {qr ? <img src={qr} alt={t("QR-Code für 2FA")} className="w-40 h-40" data-testid="twofa-qr" /> : <QrCode className="w-40 h-40 text-black" />}
            </div>
            <div className="flex-1 space-y-3">
              <p className="text-sm text-gray-300">{t("1. Öffnen Sie Ihre Authenticator-App und scannen Sie den QR-Code.")}</p>
              <div>
                <p className="text-xs text-gray-500 mb-1">{t("Oder Schlüssel manuell eingeben:")}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-black border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-gray-300 break-all" data-testid="twofa-secret">{secret}</code>
                  <button onClick={copySecret} data-testid="twofa-copy-secret" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white p-2 rounded-lg shrink-0"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-gray-300">{t("2. Geben Sie den 6-stelligen Code aus der App ein:")}</p>
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="000000" data-testid="twofa-code-input"
                className="w-40 bg-black border border-white/10 rounded-lg px-3 py-2.5 text-lg tracking-[0.3em] font-mono text-center focus:outline-none"
                style={{ caretColor: accent }} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={confirm} disabled={busy} data-testid="twofa-confirm" style={btn(accent)}
              className="font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("Aktivieren & bestätigen")}
            </button>
            <button onClick={() => { setMode("idle"); setQr(""); setSecret(""); setCode(""); }} disabled={busy} data-testid="twofa-cancel"
              className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
          </div>
        </div>
      )}

      {/* DISABLE: Passwort bestätigen */}
      {mode === "disable" && (
        <div className="space-y-3 max-w-sm" data-testid="twofa-disable-panel">
          <p className="text-sm text-gray-300 flex items-center gap-2"><Lock className="w-4 h-4 text-amber-300" /> {t("Bitte bestätigen Sie mit Ihrem Passwort, um 2FA zu deaktivieren.")}</p>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Ihr Passwort")}
            data-testid="twofa-disable-password" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400/50" />
          <div className="flex items-center gap-3">
            <button onClick={disable} disabled={busy} data-testid="twofa-disable-confirm"
              className="border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t("2FA endgültig deaktivieren")}
            </button>
            <button onClick={() => { setMode("idle"); setPassword(""); }} disabled={busy} data-testid="twofa-disable-cancel"
              className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
          </div>
        </div>
      )}
    </Card>
  );
}
